/**
 * V2 quiz create/update autosave.
 *
 * Scheduling
 * - `scheduleSaveFromState` builds a create or update body (or no-ops if the
 *   title is incomplete, there are no questions, or any question is incomplete —
 *   including missing translations). Never PUTs a filtered subset.
 * - At most one request runs at a time (`inflightRef`). A newer schedule while busy
 *   overwrites `pendingSaveRef` (latest-wins; one pending slot).
 * - A create queued before the first POST returns is rebuilt as an update once
 *   `createdQuizIdRef` is set, so the pending slot cannot POST a second quiz.
 *
 * Drain / idle signaling
 * - When a save finishes, `runSave`'s `finally` bumps `saveSettledGeneration`.
 * - An effect on that counter either starts the pending body or, if nothing is
 *   left to run, treats the pipeline as idle.
 *
 * `waitUntilIdle` (used when leaving the editor)
 * - Leave flush = user hits back / navigates away: sync focused field → abort
 *   in-flight per-field translates → batch-translate the quiz → schedule a final
 *   save → `await waitUntilIdle()` so we don't navigate until that save (and any
 *   chained pending save) has finished.
 * - If already idle → resolved Promise immediately.
 * - If busy → parks that Promise's `resolve` on `idleWaitersRef` (multiple callers
 *   can wait). When the settle effect finds no inflight and no pending, it calls
 *   every parked resolve so `await waitUntilIdle()` continues.
 */

import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
} from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import {
  createQuizMutation,
  getQuizDetailsQueryKey,
  getQuizzesQueryKey,
  type QuizCreateRequest,
  type QuizDetail,
  type QuizUpdateRequest,
  updateQuizMutation,
} from '@cfa/pathway-fe-shared/xp';

import ToastMessageBlock from '@/components/Toasts/SuccessToast';

import { queryClient } from '@/services/api';

import type { V2QuizEditorState } from './state';
import {
  hasIncompleteQuestionWork,
  isTitleComplete,
  toSavableQuestions,
} from './v2QuizValidation';

/** Required on PUT body shape; not used for title — renames use PATCH /name. */
export const PUT_QUIZ_NAME_PLACEHOLDER = { en: '-', es: '-' };

export interface SaveBody {
  create?: QuizCreateRequest;
  /**
   * `name` is a placeholder on PUT (see `PUT_QUIZ_NAME_PLACEHOLDER`).
   * Renames use patchQuizName (PATHWAY-4043) only.
   */
  update?: QuizUpdateRequest;
  quizId?: string;
  savingQuestionIndex: number | null;
  /** Stable card id for autosave UI — survives question reorder during PUT. */
  savingQuestionClientId: string | null;
}

const savingQuestionClientIdAtIndex = (
  state: V2QuizEditorState,
  questionIndex: number | null,
): string | null => {
  if (questionIndex === null) {
    return null;
  }
  return state.questions[questionIndex]?.clientId ?? null;
};

export const buildSaveBody = (
  state: V2QuizEditorState,
  savingQuestionIndex: number | null,
): SaveBody | null => {
  if (!isTitleComplete(state.name, state.primaryLanguage)) {
    return null;
  }

  // Require every question complete (bilingual) — never persist a subset.
  if (state.questions.length === 0 || hasIncompleteQuestionWork(state)) {
    return null;
  }

  const questions = toSavableQuestions(state.questions);

  const savingQuestionClientId = savingQuestionClientIdAtIndex(
    state,
    savingQuestionIndex,
  );

  if (!state.quizId) {
    return {
      savingQuestionIndex,
      savingQuestionClientId,
      create: {
        v2: true,
        primaryLanguage: state.primaryLanguage,
        ownerId: state.ownerId,
        name: {
          en: state.name.en ?? '',
          es: state.name.es ?? '',
        },
        passingGrade: state.passingGrade,
        questions,
      },
    };
  }

  return {
    savingQuestionIndex,
    savingQuestionClientId,
    quizId: state.quizId,
    update: {
      name: PUT_QUIZ_NAME_PLACEHOLDER,
      passingGrade: state.passingGrade,
      questions,
    },
  };
};

export const useV2QuizSave = (options: {
  setQuiz: Dispatch<SetStateAction<V2QuizEditorState>>;
  onCreated: (quizId: string) => void;
  locationNumber?: string;
}) => {
  const { t } = useTranslation();
  const setQuiz = options.setQuiz;
  const inflightRef = useRef(false);
  const lastSaveFailedRef = useRef(false);
  const createdQuizIdRef = useRef<string | null>(null);
  const pendingSaveRef = useRef<{
    state: V2QuizEditorState;
    savingQuestionIndex: number | null;
  } | null>(null);
  const onCreatedRef = useRef(options.onCreated);
  const locationNumberRef = useRef(options.locationNumber);
  useEffect(() => {
    onCreatedRef.current = options.onCreated;
    locationNumberRef.current = options.locationNumber;
  }, [options.onCreated, options.locationNumber]);
  const [saveSettledGeneration, setSaveSettledGeneration] = useState(0);
  const [savingQuestionClientId, setSavingQuestionClientId] = useState<
    string | null
  >(null);
  const [isSaving, setIsSaving] = useState(false);

  const showSaveError = () => {
    toast.custom(toastObj => (
      <ToastMessageBlock id={toastObj.id} severity="error">
        {t('TrainingPlans.toastMessage.errorUpdating')}
      </ToastMessageBlock>
    ));
  };

  const createQuizMutationOptions = createQuizMutation();
  const updateQuizMutationOptions = updateQuizMutation();

  const createQuiz = useMutation({
    mutationKey: createQuizMutationOptions.mutationKey,
    mutationFn: createQuizMutationOptions.mutationFn,
    onError: (error: unknown) => {
      datadogRum.addError(error);
    },
  });

  const updateQuiz = useMutation({
    mutationKey: updateQuizMutationOptions.mutationKey,
    mutationFn: updateQuizMutationOptions.mutationFn,
    onError: (error: unknown) => {
      datadogRum.addError(error);
    },
  });

  const runSave = async (body: SaveBody) => {
    lastSaveFailedRef.current = false;
    inflightRef.current = true;
    setIsSaving(true);
    setSavingQuestionClientId(body.savingQuestionClientId);
    setQuiz(prev => ({
      ...prev,
      isSaving: true,
      savingQuestionIndex: body.savingQuestionIndex,
    }));

    try {
      if (body.create) {
        const createBody: QuizCreateRequest = {
          ...body.create,
          location: locationNumberRef.current,
        };
        const data = await createQuiz.mutateAsync({
          body: createBody,
        });
        if (!data.id) {
          datadogRum.addError(new Error('Quiz create returned no id'));
          lastSaveFailedRef.current = true;
          showSaveError();
          return;
        }

        createdQuizIdRef.current = data.id;
        setQuiz(prev => ({
          ...prev,
          quizId: data.id,
        }));
        void queryClient.invalidateQueries({
          queryKey: getQuizzesQueryKey(),
          refetchType: 'all',
        });
        onCreatedRef.current(data.id);
        return;
      }

      if (body.update && body.quizId) {
        const update = body.update;
        await updateQuiz.mutateAsync({
          path: { quizId: body.quizId },
          body: update,
        });
        // staleTime: Infinity + refetchOnMount: false — keep details cache in sync
        // so leave/re-open (e.g. Navbar) does not show a pre-PUT passingGrade.
        queryClient.setQueryData<QuizDetail>(
          getQuizDetailsQueryKey({
            path: { quizId: body.quizId },
          }),
          old =>
            old
              ? {
                  ...old,
                  passingGrade: update.passingGrade,
                  questions: update.questions,
                }
              : old,
        );
      }
    } catch {
      lastSaveFailedRef.current = true;
      showSaveError();
    } finally {
      inflightRef.current = false;
      setIsSaving(false);
      setSavingQuestionClientId(null);
      setQuiz(prev => ({
        ...prev,
        isSaving: false,
        savingQuestionIndex: null,
      }));
      setSaveSettledGeneration(generation => generation + 1);
    }
  };

  const runQueuedSave = useEffectEvent((body: SaveBody) => {
    void runSave(body);
  });

  const withCreatedQuizId = (state: V2QuizEditorState) => {
    if (state.quizId || !createdQuizIdRef.current) {
      return state;
    }
    return { ...state, quizId: createdQuizIdRef.current };
  };

  const scheduleSaveFromState = (
    state: V2QuizEditorState,
    savingQuestionIndex: number | null = null,
  ): boolean => {
    const resolved = withCreatedQuizId(state);
    const body = buildSaveBody(resolved, savingQuestionIndex);
    if (!body) {
      return false;
    }

    if (inflightRef.current) {
      pendingSaveRef.current = { state: resolved, savingQuestionIndex };
      return true;
    }

    void runSave(body);
    return true;
  };

  const didLastSaveFail = () => lastSaveFailedRef.current;

  const hasInflightOrQueuedSave = () =>
    inflightRef.current || pendingSaveRef.current !== null;

  const idleWaitersRef = useRef<(() => void)[]>([]);

  const resolveIdleWaiters = () => {
    if (inflightRef.current || pendingSaveRef.current !== null) {
      return;
    }
    const waiters = idleWaitersRef.current;
    idleWaitersRef.current = [];
    waiters.forEach(waiter => waiter());
  };

  const waitUntilIdle = () => {
    if (!hasInflightOrQueuedSave()) {
      return Promise.resolve();
    }
    return new Promise<void>(resolve => {
      idleWaitersRef.current.push(resolve);
    });
  };

  useEffect(() => {
    if (saveSettledGeneration === 0) {
      return;
    }

    const pending = pendingSaveRef.current;
    if (!pending) {
      resolveIdleWaiters();
      return;
    }

    pendingSaveRef.current = null;
    const body = buildSaveBody(
      withCreatedQuizId(pending.state),
      pending.savingQuestionIndex,
    );
    if (!body) {
      resolveIdleWaiters();
      return;
    }
    if (inflightRef.current) {
      pendingSaveRef.current = pending;
      return;
    }

    runQueuedSave(body);
  }, [saveSettledGeneration]);

  return {
    scheduleSaveFromState,
    hasInflightOrQueuedSave,
    waitUntilIdle,
    didLastSaveFail,
    isSaving,
    savingQuestionClientId,
  };
};
